import type {
  ChatbotContent,
  ContentText,
  Conversation,
  Message,
  MessageContent,
  Presence,
  PresenceExited,
  PresenceJoined,
  PresenceTyping,
} from "@deskpro-product/messenger-api-schemas/messenger/api/conversation/entities";

export type MessageOrPresenceEvent = Message | PresenceJoined | PresenceExited | PresenceTyping;
export type ConversationMessages = Omit<Conversation["messages"], "items"> & {
  items: MessageOrPresenceEvent[];
};
export type ConversationWithAgentPresence = Omit<Conversation, "messages"> & {
  messages: ConversationMessages;
};

export const isMessageItem = (message: Message | Presence): message is Message =>
  ["inbound", "outbound"].includes(message.type);
export const isPresenceItem = (message: Message | Presence): message is Presence =>
  ["typing", "joined", "exited", "idle"].includes(message.type);
export const isTextContent = (content: MessageContent): content is ContentText =>
  content.type === "text";
export const isChatbotContent = (content: MessageContent): content is ChatbotContent =>
  [
    "inputChoice",
    "inputName",
    "inputEmail",
    "inputPhoneNumber",
    "inputText",
    "inputLink",
    "inputNumber",
    "inputDatetime",
    "inputFile",
  ].includes(content.type);
export function isTypingPresence(item: Message | Presence): item is PresenceTyping {
  return item.type === "typing";
}
export function isOutboundMessage(i: Message | Presence): i is Message & { type: "outbound" } {
  return i.type === "outbound";
}

export type BlobUploadSessionConstraints = {
  date_expires: string;
  constraints: {
    max_files?: number | null;
    max_file_size?: number | null;
    match_exts?: Array<string>;
    not_match_exts?: Array<string>;
  };
};

export type FileUploadException = {
  exception: string;
  file: string;
  line: number;
  trace: Array<{
    class: string;
    file: string;
    function: string;
    line: number;
    type: string;
  }>;
};

export type BlobUploadSession = {
  token: string;
  upload_url: string;
} & BlobUploadSessionConstraints;

export type FileUploadSuccess = {
  file_uuid: string;
  serve_url: string;
  download_url: string;
  errors?: string[];
} & BlobUploadSessionConstraints;

export type FileUploadResponse = FileUploadSuccess | FileUploadException;

export type OnFileUploadHandler = (
  fileUploadResponses: FileUploadResponse[],
  fileListArray: File[],
  uploadSessionId: string,
) => void;

export type FileUploadCallbacks = {
  deviceId?: string;
  conversationId?: string;
  onSuccess?: OnFileUploadHandler;
};

export type MessageAttachment = {
  thumbnail_url?: string | undefined;
  error?: boolean | undefined;
  uuid: string;
  download_url: string;
  file: File;
};
